import { useEffect, useMemo, useState } from 'react';
import { Input } from 'rsuite';

import './SearchLauncher.css';

const normalizeSearchText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const queryTokens = (query) => normalizeSearchText(query).split(/\s+/).filter(Boolean);

const isSubsequenceMatch = (needle, haystack) => {
  const n = String(needle || '');
  const h = String(haystack || '');
  if (!n || !h) return false;
  let j = 0;
  for (let i = 0; i < h.length && j < n.length; i += 1) {
    if (h[i] === n[j]) j += 1;
  }
  return j === n.length;
};

export const scoreSearchItem = (item, rawQuery) => {
  const normalizedQuery = normalizeSearchText(rawQuery);
  if (!normalizedQuery) return 0;

  const tokens = queryTokens(rawQuery);
  if (tokens.length === 0) return 0;

  const fields = [item?.title, item?.description, ...(Array.isArray(item?.keywords) ? item.keywords : [])]
    .map((v) => normalizeSearchText(v))
    .filter(Boolean);

  let score = 0;

  if (fields.some((f) => f === normalizedQuery)) score += 260;
  else if (fields.some((f) => f.includes(normalizedQuery))) score += 170;

  for (const token of tokens) {
    if (!token) continue;
    let tokenScore = 0;
    for (const field of fields) {
      if (field === token) tokenScore = Math.max(tokenScore, 52);
      else if (field.startsWith(token)) tokenScore = Math.max(tokenScore, 38);
      else if (` ${field} `.includes(` ${token} `)) tokenScore = Math.max(tokenScore, 30);
      else if (field.includes(token)) tokenScore = Math.max(tokenScore, 20);
      else if (isSubsequenceMatch(token, field.replace(/\s+/g, ''))) tokenScore = Math.max(tokenScore, 11);
    }
    score += tokenScore;
  }

  if (score > 0) score += Math.min(tokens.length, 5) * 3;
  return score;
};

/**
 * Centered search box with a keyboard-navigable results dropdown. Items are
 * `{ id, title, description, destination?, iconClass?, keywords? }`; picking one
 * calls `onSelect(item)` and clears the query.
 */
export default function SearchLauncher({
  items,
  onSelect,
  idPrefix,
  label,
  placeholder,
  resultsLabel = 'Search results',
  emptyText = 'Nothing matched your search.',
  maxResults = 9,
}) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxId = `${idPrefix}-results-listbox`;
  const optionId = (item) => `${idPrefix}-option-${item.id}`;
  const hasQuery = Boolean(String(query || '').trim());

  const results = useMemo(() => {
    const q = String(query || '').trim();
    if (!q) return [];

    return (items || [])
      .map((item) => ({ ...item, score: scoreSearchItem(item, q) }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || String(a.title).localeCompare(String(b.title)))
      .slice(0, maxResults);
  }, [items, query, maxResults]);

  useEffect(() => {
    if (!hasQuery || results.length === 0) {
      setActiveIndex(-1);
      return;
    }

    setActiveIndex((prev) => (prev >= 0 && prev < results.length ? prev : 0));
  }, [hasQuery, results.length]);

  const selectResult = (item) => {
    if (!item) return;
    setQuery('');
    onSelect?.(item);
  };

  return (
    <div className="ppc-config-home-search-wrap">
      <div className="ppc-config-settings-search">
        {label ? <div className="ppc-config-settings-search__label">{label}</div> : null}
        <div className="ppc-config-settings-search__input-wrap">
          <span className="ppc-config-settings-search__search-icon" aria-hidden="true">
            <i className="bi bi-search" />
          </span>
          <Input
            value={query}
            onChange={(value) => setQuery(value)}
            placeholder={placeholder}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-expanded={hasQuery}
            aria-autocomplete="list"
            aria-activedescendant={
              activeIndex >= 0 && activeIndex < results.length ? optionId(results[activeIndex]) : undefined
            }
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' && results.length > 0) {
                e.preventDefault();
                setActiveIndex((prev) => {
                  const next = prev + 1;
                  return next >= results.length ? 0 : next;
                });
                return;
              }

              if (e.key === 'ArrowUp' && results.length > 0) {
                e.preventDefault();
                setActiveIndex((prev) => {
                  if (prev <= 0) return results.length - 1;
                  return prev - 1;
                });
                return;
              }

              if (e.key === 'Escape') {
                if (hasQuery) {
                  e.preventDefault();
                  setQuery('');
                  setActiveIndex(-1);
                }
                return;
              }

              if (e.key === 'Enter' && results.length > 0) {
                e.preventDefault();
                const idx = activeIndex >= 0 && activeIndex < results.length ? activeIndex : 0;
                selectResult(results[idx]);
              }
            }}
          />
        </div>

        {hasQuery ? (
          <div
            id={listboxId}
            className="ppc-config-settings-search__dropdown"
            role="listbox"
            aria-label={resultsLabel}
          >
            {results.length > 0 ? (
              results.map((item, idx) => (
                <button
                  key={`${idPrefix}-${item.id}`}
                  id={optionId(item)}
                  type="button"
                  role="option"
                  aria-selected={idx === activeIndex}
                  className={`ppc-config-settings-search__item${idx === activeIndex ? ' is-active' : ''}`}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => selectResult(item)}
                >
                  <span className="ppc-config-settings-search__item-icon" aria-hidden="true">
                    <i className={item.iconClass || 'bi bi-sliders'} />
                  </span>
                  <span className="ppc-config-settings-search__item-content">
                    <span className="ppc-config-settings-search__item-title">{item.title}</span>
                    <span className="ppc-config-settings-search__item-description">{item.description}</span>
                  </span>
                  {item.destination ? (
                    <span className="ppc-config-settings-search__item-destination">{item.destination}</span>
                  ) : null}
                </button>
              ))
            ) : (
              <div className="ppc-config-settings-search__empty">{emptyText}</div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
